import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const WARN_BEFORE = 60 * 1000; // show the warning 60s before

export default function IdleGuard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lastActivity = useRef(Date.now());
  const [secsLeft, setSecsLeft] = useState(0); // 0 = hidden
  const [limitMs, setLimitMs] = useState(15 * 60 * 1000); // default 15 min, admin-configurable

  useEffect(() => {
    if (!user) return;
    api.get("/settings/security")
      .then((r) => setLimitMs((r.data.idle_timeout_minutes || 15) * 60 * 1000))
      .catch(() => {});
  }, [user]);

  const signOut = useCallback(async () => {
    await logout();
    navigate("/login");
    toast("Signed out for security", { description: "Your session ended after a period of inactivity." });
  }, [logout, navigate]);

  const bump = useCallback(() => { lastActivity.current = Date.now(); setSecsLeft(0); }, []);

  useEffect(() => {
    if (!user) return undefined;
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    let throttle = null;
    const onActivity = () => {
      if (throttle) return;
      throttle = setTimeout(() => { throttle = null; lastActivity.current = Date.now(); }, 1000);
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const iv = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= limitMs) signOut();
      else if (idle >= limitMs - WARN_BEFORE) setSecsLeft(Math.max(1, Math.ceil((limitMs - idle) / 1000)));
      else setSecsLeft(0);
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(iv);
      if (throttle) clearTimeout(throttle);
    };
  }, [user, signOut, limitMs]);

  if (!user || !secsLeft) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="idle-warning">
      <div className="glass glass-cyan rounded-2xl p-6 max-w-sm w-full text-center mx-4">
        <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <h2 className="font-head font-bold text-lg">Still there?</h2>
        <p className="text-sm text-muted-foreground mt-2 font-mono">
          To protect patient data you'll be signed out in{" "}
          <span className="text-cyan-300 font-semibold" data-testid="idle-countdown">{secsLeft}s</span>.
        </p>
        <Button onClick={bump} data-testid="idle-stay-btn" className="mt-4 w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
          Stay signed in
        </Button>
      </div>
    </div>
  );
}
